import { useState, useEffect } from 'react';
import Card from 'components/card';
import SalesForceImg from 'assets/salesforce.png';
import HubspotImg from 'assets/hubspot.png';
import PipedriveImg from 'assets/pipedrive.png';
import ActiveCampaingImg from 'assets/activecampaign.png';
import MondayImg from 'assets/monday.png';
import QhareImg from 'assets/quare.jpg';
import GsheetImg from 'assets/gsheet.png';
import CheelyImg from 'assets/chelly.svg';
import { useDisclosure } from '@chakra-ui/hooks';
import ApiKeyModal from './components/ApiKeyModal';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Integrate = () => {
    const [loading, setLoading] = useState(false);
    const [selectedService, setSelectedService] = useState<{
        name: string;
        service: string;
    } | null>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [services, setServices] = useState([
        {
            name: 'Qhare',
            service: 'Qhare',
            description:
                'Plateforme complète dédiée aux professionnels du bâtiment, spécialisés dans les énergies renouvelables et la rénovation énergétique.',
            logo: QhareImg,
            status: 'not-setup',
            onLogin: () => {
                setSelectedService({ name: 'Qhare', service: 'Qhare' });
                onOpen();
            },
        },
        {
            name: 'SalesForce',
            service: 'meta',
            description:
                'Recevez vos leads directement dans votre compte SalesForce.',
            logo: SalesForceImg,
            status: 'coming-soon',
            onLogin: () => {},
        },
        {
            name: 'Chelly',
            service: 'chelly',
            description:
                'Recevez vos leads directement dans votre compte Chelly.',
            logo: CheelyImg,
            status: 'coming-soon',
            onLogin: () => {},
        },
        {
            name: 'Hubspot',
            service: 'gmb',
            description:
                'Recevez vos leads directement dans votre compte Hubspot.',
            logo: HubspotImg,
            status: 'coming-soon',
            onLogin: () => {},
        },
        {
            name: 'PipeDrive',
            service: 'fork',
            description:
                'Recevez vos leads directement dans votre compte PipeDrive.',
            logo: PipedriveImg,
            status: 'coming-soon',
            onLogin: () => {},
        },
        {
            name: 'Monday',
            service: 'fork',
            description:
                'Recevez vos leads directement dans votre compte Monday.',
            logo: MondayImg,
            status: 'coming-soon',
            onLogin: () => {},
        },
        {
            name: 'Active Campaign',
            service: 'fork',
            description:
                'Recevez vos leads directement dans votre compte Active Campaign.',
            logo: ActiveCampaingImg,
            status: 'coming-soon',
            onLogin: () => {},
        },
        {
            name: 'Google Sheet',
            service: 'gsheet',
            description:
                'Recevez vos leads directement depuis votre compte Google Sheet.',
            logo: GsheetImg,
            status: 'coming-soon',
            onLogin: () => {},
        },
    ]);

    // Check integration status on component mount
    useEffect(() => {
        checkIntegrations();
    }, []);

    const checkIntegrations = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${CONFIG.apiUrl}/integration/check/Qhare`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.success && response.data.exists) {
                // Update Qhare service status
                setServices((prevServices) =>
                    prevServices.map((service) =>
                        service.service === 'Qhare'
                            ? {
                                  ...service,
                                  status: response.data.isActive
                                      ? 'online'
                                      : 'offline',
                              }
                            : service
                    )
                );
            }
        } catch (error) {
            console.error('Error checking integrations:', error);
        }
    };

    const handleSaveApiKey = async (apiKey: string) => {
        if (!selectedService) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${CONFIG.apiUrl}/integration/api-key`,
                {
                    service: selectedService.service,
                    apiKey: apiKey,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.success) {
                toast.success('Clé API enregistrée avec succès!');
                onClose();
                // Update service status
                setServices((prevServices) =>
                    prevServices.map((service) =>
                        service.service === selectedService.service
                            ? { ...service, status: 'online' }
                            : service
                    )
                );
            }
        } catch (error: any) {
            toast.error(
                error.response?.data?.error ||
                    "Erreur lors de l'enregistrement de la clé API"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDisconnect = async (serviceName: string) => {
        if (
            !window.confirm(
                `Êtes-vous sûr de vouloir déconnecter ${serviceName}?`
            )
        ) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(
                `${CONFIG.apiUrl}/integration/${serviceName}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.success) {
                toast.success('Intégration supprimée avec succès!');
                // Update service status back to not-setup
                setServices((prevServices) =>
                    prevServices.map((service) =>
                        service.service === serviceName
                            ? { ...service, status: 'not-setup' }
                            : service
                    )
                );
            }
        } catch (error: any) {
            toast.error(
                error.response?.data?.error ||
                    "Erreur lors de la déconnexion de l'intégration"
            );
        }
    };

    const PluginCard = (props: {
        logo: any;
        name: string;
        service: string;
        description: string;
        status?: string;
        index: number;
        onLogin?: () => void;
        onDisconnect?: (service: string) => void;
    }) => {
        const {
            logo,
            name,
            description,
            service,
            status,
            onLogin,
            onDisconnect,
            index,
        } = props;

        return (
            <Card
                extra={'relative w-full mt-4 px-2 py-6 border'}
                index={index + 1}
                key={index + 1}
            >
                {/* Badge */}
                {status === 'online' && (
                    <div className="absolute right-3 top-3 rounded-lg bg-green-600/80 px-2 py-1 text-xs text-white">
                        {'Connecté'}
                    </div>
                )}
                {status === 'offline' && (
                    <div className="absolute right-3 top-3 rounded-lg bg-red-600/80 px-2 py-1 text-xs text-white">
                        {'Déconnecté'}
                    </div>
                )}
                {status === 'not-setup' && (
                    <div className="absolute right-3 top-3 rounded-lg bg-orange-600/80 px-2 py-1 text-xs text-white">
                        {'Non configuré'}
                    </div>
                )}
                {/* Header */}
                <div className="w-full px-1">
                    <img
                        src={logo}
                        alt={`${name}-logo`}
                        className="ml-1 h-[70px] w-[70px] rounded-lg border"
                    />
                    <h4 className="mt-4 text-xl font-medium">{name}</h4>
                    <p className="mt-2 text-xs">{description}</p>
                    {service === 'Qhare' && (
                        <a
                            href="https://crm-Qhare.fr"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-block text-xs text-brand-500 hover:text-brand-600 hover:underline"
                        >
                            Accéder à Qhare CRM →
                        </a>
                    )}
                </div>
                <div className="mt-4 flex justify-center">
                    {status === 'coming-soon' && (
                        <button
                            className={`linear flex h-10 w-full cursor-not-allowed items-center justify-center rounded-xl bg-leadrs px-5 py-3 text-sm font-medium text-white`}
                        >
                            Bientôt disponible
                        </button>
                    )}
                    {status === 'not-setup' && (
                        <button
                            className={`linear flex h-10 w-full cursor-pointer items-center justify-center rounded-xl bg-leadrs px-5 py-3 text-sm font-medium text-white`}
                            onClick={onLogin}
                        >
                            Associer un compte
                        </button>
                    )}
                    {status === 'online' && (
                        <div className="flex w-full gap-2">
                            <p
                                className={`linear flex h-10 flex-1 cursor-pointer items-center justify-center rounded-xl text-sm font-medium text-red transition duration-200`}
                                onClick={() => onDisconnect?.(service)}
                            >
                                Déconnecter
                            </p>
                        </div>
                    )}
                    {status === 'offline' && (
                        <button
                            className={`linear bg-primary flex h-10 w-full cursor-pointer items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-white`}
                            onClick={onLogin}
                        >
                            Me reconnecter
                        </button>
                    )}
                </div>
            </Card>
        );
    };

    return (
        <div>
            <ToastContainer />
            <ApiKeyModal
                isOpen={isOpen}
                onClose={onClose}
                serviceName={selectedService?.name || ''}
                onSave={handleSaveApiKey}
                loading={loading}
            />
            {loading ? (
                <div className="rounded-xl border-2 border-[#1f2937] p-10">
                    <p className="font-han mb-[80px] text-center text-2xl">
                        Chargement des extensions...
                    </p>
                </div>
            ) : (
                <>
                    <Card
                        extra={
                            'w-full pb-[10px] pt-[10px] px-[29px] h-fit shadow shadow-3xl rounded-[20px] border'
                        }
                    >
                        {/* Header */}
                        <div className="mt-1">
                            <p className="text-xl font-semibold text-navy-700 dark:text-white">
                                Intégrations 🔑
                            </p>
                            <p className="mt-2 text-base text-gray-600">
                                Connectez votre CRM à Leadrs pour recevoir
                                automatiquement vos leads, simplifiant ainsi la
                                gestion et le suivi de vos prospects.
                            </p>
                        </div>
                    </Card>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                        {services.map((service, index) => (
                            <PluginCard
                                index={index}
                                key={service.service}
                                name={service.name}
                                service={service.service}
                                description={service.description}
                                logo={service.logo}
                                status={service.status}
                                onLogin={service.onLogin}
                                onDisconnect={handleDisconnect}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default Integrate;
